import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Badge from '../UI/Badge';
import AssignmentSubmit from './AssignmentSubmit';

export default function AssignmentDetail({ userRole }) {
  const { courseId, assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  useEffect(() => {
    fetchAssignment();
  }, [assignmentId]);

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/assignments/${assignmentId}`);
      setAssignment(response.data.assignment);

      if (userRole === 'STUDENT') {
        // Fetch student's submission
        const subResponse = await api.get(`/api/assignments/${assignmentId}/my-submission`);
        setSubmission(subResponse.data.submission);
      } else if (userRole === 'TEACHER') {
        // Fetch all submissions
        const subsResponse = await api.get(`/api/assignments/${assignmentId}/submissions`);
        setSubmissions(subsResponse.data.submissions || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || '과제를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmissionSuccess = () => {
    setShowSubmitForm(false);
    fetchAssignment();
  };

  const getStatusInfo = () => {
    if (!assignment.dueDate) return { text: '기한 없음', variant: 'default' };

    const now = new Date();
    const dueDate = new Date(assignment.dueDate);

    if (dueDate < now) {
      return { text: '마감', variant: 'danger' };
    }

    const daysLeft = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 3) {
      return { text: `D-${daysLeft}`, variant: 'warning' };
    }

    return { text: '진행중', variant: 'success' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <Card className="text-center py-8">
        <p className="text-danger-500 mb-4">{error || '과제를 찾을 수 없습니다.'}</p>
        <Button onClick={() => navigate(`/courses/${courseId}`)}>수업으로 돌아가기</Button>
      </Card>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div className="space-y-6">
      {/* Assignment Info */}
      <Card>
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-3xl font-bold text-neutral-800 flex-1">{assignment.title}</h1>
          <Badge variant={statusInfo.variant}>{statusInfo.text}</Badge>
        </div>

        <div className="prose max-w-none mb-6">
          <p className="text-neutral-700 whitespace-pre-wrap">{assignment.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-neutral-100">
          {assignment.dueDate && (
            <div>
              <p className="text-sm text-neutral-500">마감일</p>
              <p className="font-medium text-neutral-800">
                {new Date(assignment.dueDate).toLocaleString('ko-KR')}
              </p>
            </div>
          )}
          {assignment.maxScore && (
            <div>
              <p className="text-sm text-neutral-500">배점</p>
              <p className="font-medium text-neutral-800">{assignment.maxScore}점</p>
            </div>
          )}
          {userRole === 'TEACHER' && (
            <div>
              <p className="text-sm text-neutral-500">제출 현황</p>
              <p className="font-medium text-neutral-800">
                {submissions.length}명 제출
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Student View: Submission */}
      {userRole === 'STUDENT' && (
        <Card>
          <h2 className="text-xl font-bold text-neutral-800 mb-4">내 제출</h2>

          {submission ? (
            <div className="space-y-4">
              <div className="p-4 bg-secondary-50 rounded-xl">
                <p className="text-sm text-secondary-700 font-medium mb-2">제출 완료</p>
                <p className="text-neutral-700 whitespace-pre-wrap">{submission.content}</p>
                {submission.fileUrl && (
                  <a
                    href={submission.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline mt-2 inline-block"
                  >
                    첨부파일 보기
                  </a>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-neutral-500">
                <span>제출일: {new Date(submission.submittedAt).toLocaleString('ko-KR')}</span>
                {submission.score !== null && (
                  <Badge variant="primary">점수: {submission.score}점</Badge>
                )}
              </div>
            </div>
          ) : showSubmitForm ? (
            <AssignmentSubmit
              assignmentId={assignmentId}
              onSuccess={handleSubmissionSuccess}
              onCancel={() => setShowSubmitForm(false)}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-neutral-500 mb-4">아직 제출하지 않았습니다.</p>
              <Button onClick={() => setShowSubmitForm(true)}>과제 제출하기</Button>
            </div>
          )}
        </Card>
      )}

      {/* Teacher View: Submissions List */}
      {userRole === 'TEACHER' && (
        <Card>
          <h2 className="text-xl font-bold text-neutral-800 mb-4">제출 현황</h2>

          {submissions.length === 0 ? (
            <p className="text-center text-neutral-500 py-8">아직 제출한 학생이 없습니다.</p>
          ) : (
            <div className="space-y-3">
              {submissions.map((sub) => (
                <Card key={sub.id} padding="sm" className="bg-neutral-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-neutral-800">
                        {sub.student?.username || '알 수 없음'}
                      </p>
                      <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{sub.content}</p>
                      {sub.fileUrl && (
                        <a
                          href={sub.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline text-sm mt-2 inline-block"
                        >
                          첨부파일
                        </a>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm text-neutral-500">
                        {new Date(sub.submittedAt).toLocaleDateString('ko-KR')}
                      </p>
                      {sub.score !== null ? (
                        <Badge variant="primary" className="mt-2">{sub.score}점</Badge>
                      ) : (
                        <Badge variant="default" className="mt-2">미채점</Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
